// Localização: frontend/src/components/Prontuario/Profissional/RH/SalarioFormModal.jsx (versão 1.0.0)
// Modal de formulário para criar/editar salário

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createSalario } from '../../../../api/api';

const SalarioFormModal = ({ isOpen, onClose, profissionalId, onSuccess }) => {
    const [formData, setFormData] = useState({
        valor_salario: '',
        data_inicio: '',
        data_fim: '',
        tipo_salario: 'mensal',
        valor_hora: '',
        carga_horaria_mensal: '',
        motivo_alteracao: '',
        observacoes: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const dados = {
                ...formData,
                valor_salario: parseFloat(formData.valor_salario),
                valor_hora: formData.valor_hora ? parseFloat(formData.valor_hora) : null,
                carga_horaria_mensal: formData.carga_horaria_mensal ? parseInt(formData.carga_horaria_mensal) : null,
                data_fim: formData.data_fim || null
            };

            const response = await createSalario(profissionalId, dados);
            if (response.data.success) {
                onSuccess();
            } else {
                setError(response.data.message || 'Erro ao criar salário.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao criar salário.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900">Novo Salário</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Valor do Salário *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                required
                                value={formData.valor_salario}
                                onChange={(e) => setFormData({ ...formData, valor_salario: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                placeholder="0.00"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Data de Início *
                            </label>
                            <input
                                type="date"
                                required
                                value={formData.data_inicio}
                                onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Data de Fim (opcional)
                            </label>
                            <input
                                type="date"
                                value={formData.data_fim}
                                onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Deixe vazio se for o salário atual</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tipo de Salário *
                            </label>
                            <select
                                required
                                value={formData.tipo_salario}
                                onChange={(e) => setFormData({ ...formData, tipo_salario: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            >
                                <option value="mensal">Mensal</option>
                                <option value="diario">Diário</option>
                                <option value="horista">Horista</option>
                                <option value="comissao">Comissão</option>
                            </select>
                        </div>

                        {formData.tipo_salario === 'horista' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Valor por Hora
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.valor_hora}
                                    onChange={(e) => setFormData({ ...formData, valor_hora: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    placeholder="0.00"
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Carga Horária Mensal
                            </label>
                            <input
                                type="number"
                                value={formData.carga_horaria_mensal}
                                onChange={(e) => setFormData({ ...formData, carga_horaria_mensal: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                placeholder="160"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Motivo da Alteração
                        </label>
                        <input
                            type="text"
                            value={formData.motivo_alteracao}
                            onChange={(e) => setFormData({ ...formData, motivo_alteracao: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            placeholder="Ex: Ajuste salarial anual"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Observações
                        </label>
                        <textarea
                            value={formData.observacoes}
                            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            placeholder="Observações adicionais..."
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SalarioFormModal;

